import clsx from 'clsx'
import Link from 'next/link'
import { useCallback, useEffect, useRef, useState } from 'react'

import { BASE_URL } from '@/shared/lib/const/env'
import { useMediaQueries } from '@/shared/lib/hooks/useMediaQueries'
import { Image } from '@/shared/ui/Image'

import cl from './ObserverList.module.scss'

export type ObserverListItem = {
	name: string
	image?: string | false
	link: string
}

export type ObserverListProps = {
	items: ObserverListItem[]
	scrollOffset?: number
}

const DEFAULT_SCROLL_OFFSET = 340

const isAnchorLink = (link: string): boolean => link.startsWith('#')

const getAnchorId = (link: string): string => link.slice(1)

export const ObserverList = ({ items }: ObserverListProps): JSX.Element => {
	const [activeAnchor, setActiveAnchor] = useState<string | null>(null)
	const [isSticky, setIsSticky] = useState(false)
	const listRef = useRef<HTMLUListElement>(null)
	const fixedListRef = useRef<HTMLUListElement>(null)
	const itemRefs = useRef<Map<string, HTMLLIElement>>(new Map())
	const rootRef = useRef<HTMLElement>(null)
	const isStickyRef = useRef(false)
	const { isDesktop } = useMediaQueries()
	const scrollFinalOffset = isDesktop ? DEFAULT_SCROLL_OFFSET : 190
	console.log(scrollFinalOffset)

	const hasImages = items?.some(item => typeof item.image === 'string' && item.image)

	const scrollToAnchor = useCallback(
		(anchorId: string): void => {
			const targetElement = document.getElementById(anchorId)
			if (!targetElement) return

			const elementPosition = targetElement.getBoundingClientRect().top + window.scrollY
			const offsetPosition = elementPosition - scrollFinalOffset

			window.scrollTo({
				top: offsetPosition,
				behavior: 'smooth'
			})
		},
		[scrollFinalOffset]
	)

	const handleAnchorClick = useCallback(
		(e: React.MouseEvent, link: string): void => {
			if (isAnchorLink(link)) {
				e.preventDefault()
				const anchorId = getAnchorId(link)
				scrollToAnchor(anchorId)
			}
		},
		[scrollToAnchor]
	)

	useEffect(() => {
		if (!items) return
		const anchorItems = items.filter(item => isAnchorLink(item.link))
		if (anchorItems?.length === 0) return

		const observers: IntersectionObserver[] = []

		anchorItems.forEach(({ link }) => {
			const anchorId = getAnchorId(link)
			const targetElement = document.getElementById(anchorId)
			if (!targetElement) return

			const observer = new IntersectionObserver(
				entries => {
					const entry = entries[0]
					if (entry.isIntersecting) {
						setActiveAnchor(anchorId)
					}
				},
				{
					rootMargin: `-${scrollFinalOffset}px 0px 0px 0px`,
					threshold: 1.0
				}
			)

			observer.observe(targetElement)
			observers.push(observer)
		})

		return () => {
			observers.forEach(observer => observer.disconnect())
		}
	}, [items, scrollFinalOffset])

	useEffect(() => {
		if (!activeAnchor) return

		const scrollListToItem = (list: HTMLUListElement | null, itemIndex: number) => {
			if (!list) return
			const item = list.children[itemIndex] as HTMLElement
			if (!item) return

			const listRect = list.getBoundingClientRect()
			const itemRect = item.getBoundingClientRect()

			const itemCenter = itemRect.left + itemRect.width / 2
			const listCenter = listRect.left + listRect.width / 2
			const offset = itemCenter - listCenter

			list.scrollTo({
				left: list.scrollLeft + offset,
				behavior: 'smooth'
			})
		}

		const activeIndex = items.findIndex(item => {
			if (!isAnchorLink(item.link)) return false
			return getAnchorId(item.link) === activeAnchor
		})

		if (activeIndex !== -1) {
			scrollListToItem(listRef.current, activeIndex)
			scrollListToItem(fixedListRef.current, activeIndex)
		}
	}, [activeAnchor, items])

	useEffect(() => {
		if (!hasImages || typeof window === 'undefined') return

		const MOBILE_BREAKPOINT = 1150

		const checkSticky = () => {
			if (window.innerWidth >= MOBILE_BREAKPOINT) {
				if (isStickyRef.current) {
					isStickyRef.current = false
					setIsSticky(false)
				}
				return
			}

			const root = rootRef.current
			if (!root) return

			const rect = root.getBoundingClientRect()
			const isOutOfView = rect.bottom < 0

			if (!isStickyRef.current && isOutOfView) {
				isStickyRef.current = true
				setIsSticky(true)
			} else if (isStickyRef.current && !isOutOfView) {
				isStickyRef.current = false
				setIsSticky(false)
			}
		}

		window.addEventListener('scroll', checkSticky, { passive: true })
		window.addEventListener('resize', checkSticky, { passive: true })
		checkSticky()

		return () => {
			window.removeEventListener('scroll', checkSticky)
			window.removeEventListener('resize', checkSticky)
		}
	}, [hasImages])

	const setItemRef = (link: string) => (el: HTMLLIElement | null) => {
		if (el) {
			const key = isAnchorLink(link) ? getAnchorId(link) : link
			itemRefs.current.set(key, el)
		}
	}

	const renderItem = (item: ObserverListItem, showImage = true) => {
		const { name, image, link } = item
		const isAnchor = isAnchorLink(link)
		const anchorId = isAnchor ? getAnchorId(link) : null
		const isActive = isAnchor && anchorId === activeAnchor
		const imageSrc = showImage && image ? BASE_URL + image : null

		const content = (
			<>
				{imageSrc && (
					<div className={cl.itemImage}>
						<Image alt={name} src={imageSrc} width={120} height={120} />
					</div>
				)}
				<span className={cl.itemLabel}>{name}</span>
			</>
		)

		const itemClassName = clsx({
			[cl.item]: !imageSrc,
			[cl.itemActive]: !imageSrc && isActive,
			[cl.itemWithImage]: imageSrc,
			[cl.itemWithImageActive]: imageSrc && isActive
		})

		return (
			<li key={link} ref={showImage ? setItemRef(link) : undefined} className={itemClassName}>
				<Link prefetch={false} href={link} onClick={e => handleAnchorClick(e, link)}>
					{content}
				</Link>
			</li>
		)
	}

	return (
		<>
			{/* Основное меню с картинками - sticky на десктопе, static на мобильных */}
			<section ref={rootRef} className={cl.root}>
				<ul ref={listRef} className={clsx(cl.list, { [cl.listWithImages]: hasImages })}>
					{items?.map(item => renderItem(item, true))}
				</ul>
			</section>

			{/* Фиксированное меню без картинок - только на мобильных при скролле */}
			{hasImages && (
				<section className={clsx(cl.fixedMenu, { [cl.fixedMenuVisible]: isSticky })}>
					<ul ref={fixedListRef} className={cl.list}>
						{items.map(item => renderItem(item, false))}
					</ul>
				</section>
			)}
		</>
	)
}
